import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faExclamationTriangle,
  faPencilAlt,
  faBan,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import LoadIssuesModal from './LoadIssuesModal';

const IssueListModal = ({
  isOpen,
  onClose,
  loadId,
  issues,
  onEditClick,
  onLoadDetailClick,
  onCancelDriverClick,
}) => {

  const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
  const [selectedLoadForIssues, setSelectedLoadForIssues] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getReportTypeLabel = (type) => {
    const types = {
      'cancel': 'İptal',
      'revision': 'Revizyon',
      'other': 'Diğer'
    };
    return types[type] || type;
  };

  const getReportTypeColor = (type) => {
    const colors = {
      'cancel': 'bg-red-100 text-red-800',
      'revision': 'bg-yellow-100 text-yellow-800',
      'other': 'bg-blue-100 text-blue-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'pending': 'Beklemede',
      'resolved': 'Çözüldü',
      'rejected': 'Reddedildi'
    };
    return statuses[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-orange-100 text-orange-800',
      'resolved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleDetailClick = (issue) => {
    setIsIssuesModalOpen(true);
    setSelectedLoadForIssues(issue.load_id);
  };

  const handleEditClick = (issue) => {
    onEditClick(issue);
    onClose();
  }

  const handleLoadDetailClick = (issue) => {
    onLoadDetailClick(issue);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 1040 }}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />
              Sorun Bildirimleri
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {!issues || issues.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="mb-4 opacity-30" />
                <p className="text-lg">Henüz sorun bildirimi bulunmamaktadır.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {issues.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getReportTypeColor(item.issue.report_type)}`}>
                          {getReportTypeLabel(item.issue.report_type)}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                          {getStatusLabel(item.status)}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDetailClick(item)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faEye} />
                        Ayrıntı
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Yük ID</p>
                        <p className="font-semibold text-gray-800">{item.issue.load_id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Oluşturulma Tarihi</p>
                        <p className="font-medium text-gray-700">{formatDate(item.created_at)}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Açıklama</p>
                      <p className="text-gray-800 bg-gray-50 p-3 rounded border border-gray-200">
                        {item.issue.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Toplam <span className="font-semibold">{issues?.length || 0}</span> sorun bildirimi
              </p>
              <button
                onClick={onClose}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {isIssuesModalOpen && (
        <LoadIssuesModal
          isOpen={isIssuesModalOpen}
          onClose={() => setIsIssuesModalOpen(false)}
          loadId={selectedLoadForIssues}
          onEditClick={handleEditClick}
          onDetailsClick={handleLoadDetailClick}
          onCancelDriverClick={onCancelDriverClick}
        />
      )}
    </>
  );
};

export default IssueListModal;